import { Toaster } from "@/components/ui/toaster";
import { ReloadPrompt } from "@/components/ReloadPrompt";
import { ExportProgressProvider } from "@/components/appraisal/document/ExportProgressOverlay";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { getSubdomainInfo } from "@/lib/subdomainRouter";
import { getSiteUrl } from "@/lib/siteUrl";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import { TeamProvider } from "@/contexts/TeamContext";
import { AdminViewAsProvider } from "@/contexts/AdminViewAsContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { VerifiedRoute } from "@/components/VerifiedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { OfflineBanner } from "@/components/OfflineBanner";
import { CookieConsent } from "@/components/CookieConsent";
import { TourProvider } from "@/contexts/TourContext";
import { Suspense } from "react";
import { lazyRetry } from "@/lib/lazyRetry";
import { PageLoader } from "@/components/ui/brand-loader";
const IS_PWA = window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as any).standalone === true;

const SubdomainSiteRouter = lazyRetry(() => import("@/components/SubdomainSiteRouter").then(m => ({ default: m.SubdomainSiteRouter })));

const LandingPage = lazyRetry(() => import("./pages/LandingPage"));
// Dev-only design previews (no auth, fixture data)
const DevShellPreview = import.meta.env.DEV ? lazyRetry(() => import("./pages/DevShellPreview")) : null;
const DevSketchV2 = import.meta.env.DEV ? lazyRetry(() => import("./pages/DevSketchV2")) : null;
const DevPaletteShowroom = import.meta.env.DEV ? lazyRetry(() => import("./pages/DevPaletteShowroom")) : null;
const DevBrandDirections = import.meta.env.DEV ? lazyRetry(() => import("./pages/DevBrandDirections")) : null;
const DevMarkLab = import.meta.env.DEV ? lazyRetry(() => import("./pages/DevMarkLab")) : null;
const DevReportSample = import.meta.env.DEV ? lazyRetry(() => import("./pages/DevReportSample")) : null;
const DevLLMNarrative = import.meta.env.DEV ? lazyRetry(() => import("./pages/DevLLMNarrative")) : null;
const Login = lazyRetry(() => import("./pages/Login"));
const ForgotPassword = lazyRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const Profile = lazyRetry(() => import("./pages/Profile"));
const Settings = lazyRetry(() => import("./pages/Settings"));
const Analytics = lazyRetry(() => import("./pages/Analytics"));
const Appraisals = lazyRetry(() => import("./pages/Appraisals"));
const AppraisalEditor = lazyRetry(() => import("./pages/AppraisalEditor"));
const EditorPanels = lazyRetry(() => import("./components/appraisal/EditorPanels"));
const SharedAppraisal = lazyRetry(() => import("./pages/SharedAppraisal"));
const SharedEngagement = lazyRetry(() => import("./pages/SharedEngagement"));
const SharedReport = lazyRetry(() => import("./pages/SharedReport"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const PendingHome = lazyRetry(() => import("./pages/PendingHome"));

// Lazy-loaded pages to reduce initial bundle size
const AppraisalLog = lazyRetry(() => import("./pages/AppraisalLog"));
const EngagementLog = lazyRetry(() => import("./pages/EngagementLog"));
const TemplateBuilder = lazyRetry(() => import("./pages/TemplateBuilder"));
const ComponentLibrary = lazyRetry(() => import("./pages/ComponentLibrary"));
const Team = lazyRetry(() => import("./pages/Team"));
const EngagementEditor = lazyRetry(() => import("./pages/EngagementEditor"));
const Admin = lazyRetry(() => import("./pages/Admin"));
const Company = lazyRetry(() => import("./pages/Company"));
const SiteBuilder = lazyRetry(() => import("./pages/SiteBuilder"));
const MyBusiness = lazyRetry(() => import("./pages/MyBusiness"));
const PublicSite = lazyRetry(() => import("./pages/PublicSite"));
const PublicSiteBlog = lazyRetry(() => import("./pages/PublicSiteBlog"));
const PublicSiteBlogPost = lazyRetry(() => import("./pages/PublicSiteBlogPost"));
const AppraiserDirectory = lazyRetry(() => import("./pages/AppraiserDirectory"));
const AppraiserDirectoryProfile = lazyRetry(() => import("./pages/AppraiserDirectoryProfile"));
const Leads = lazyRetry(() => import("./pages/Leads"));
const Calendar = lazyRetry(() => import("./pages/Calendar"));
const Notifications = lazyRetry(() => import("./pages/Notifications"));
const Portfolios = lazyRetry(() => import("./pages/Portfolios"));
const AppraisalTrash = lazyRetry(() => import("./pages/AppraisalTrash"));
const PortfolioDetail = lazyRetry(() => import("./pages/PortfolioDetail"));
const Mileage = lazyRetry(() => import("./pages/Mileage"));
const TraineeExperienceLog = lazyRetry(() => import("./pages/TraineeExperienceLog"));
const Guides = lazyRetry(() => import("./pages/Guides"));
const AppraisalTools = lazyRetry(() => import("./pages/AppraisalTools"));
const TaxAppealFeasibility = lazyRetry(() => import("./pages/TaxAppealFeasibility"));
const StandaloneSketch = lazyRetry(() => import("./pages/StandaloneSketch"));
const StandaloneDCF = lazyRetry(() => import("./pages/StandaloneDCF"));
const MarketDataReport = lazyRetry(() => import("./pages/MarketDataReport"));
const PropertyRecap = lazyRetry(() => import("./pages/PropertyRecap"));
const Referrals = lazyRetry(() => import("./pages/Referrals"));
const Features = lazyRetry(() => import("./pages/Features"));
const Pricing = lazyRetry(() => import("./pages/Pricing"));
const FindAnAppraiser = lazyRetry(() => import("./pages/public/FindAnAppraiser"));
const ForAppraisers = lazyRetry(() => import("./pages/public/ForAppraisers"));
const GetFound = lazyRetry(() => import("./pages/public/GetFound"));
const WhyAshlar = lazyRetry(() => import("./pages/public/WhyAshlar"));
const ForAttorneys = lazyRetry(() => import("./pages/public/ForAttorneys"));
const ForHomeowners = lazyRetry(() => import("./pages/public/ForHomeowners"));
const ForInvestors = lazyRetry(() => import("./pages/public/ForInvestors"));
const ForCpas = lazyRetry(() => import("./pages/public/ForCpas"));
const EstateAppraisal = lazyRetry(() => import("./pages/public/EstateAppraisal"));
const DivorceAppraisal = lazyRetry(() => import("./pages/public/DivorceAppraisal"));
const PropertyTaxAppeal = lazyRetry(() => import("./pages/public/PropertyTaxAppeal"));
const PreListingAppraisal = lazyRetry(() => import("./pages/public/PreListingAppraisal"));
const PmiRemovalAppraisal = lazyRetry(() => import("./pages/public/PmiRemovalAppraisal"));
const RetrospectiveAppraisal = lazyRetry(() => import("./pages/public/RetrospectiveAppraisal"));
const LitigationAppraisal = lazyRetry(() => import("./pages/public/LitigationAppraisal"));
const GiftTaxAppraisal = lazyRetry(() => import("./pages/public/GiftTaxAppraisal"));
const RelocationAppraisal = lazyRetry(() => import("./pages/public/RelocationAppraisal"));
const BankruptcyAppraisal = lazyRetry(() => import("./pages/public/BankruptcyAppraisal"));
const LandAppraisal = lazyRetry(() => import("./pages/public/LandAppraisal"));
const CommercialAppraisal = lazyRetry(() => import("./pages/public/CommercialAppraisal"));
const PurchaseAppraisal = lazyRetry(() => import("./pages/public/PurchaseAppraisal"));
const RefinanceAppraisal = lazyRetry(() => import("./pages/public/RefinanceAppraisal"));
const PortfolioAppraisal = lazyRetry(() => import("./pages/public/PortfolioAppraisal"));
const OfficeBuildingAppraisal = lazyRetry(() => import("./pages/public/OfficeBuildingAppraisal"));
const RetailPropertyAppraisal = lazyRetry(() => import("./pages/public/RetailPropertyAppraisal"));
const IndustrialPropertyAppraisal = lazyRetry(() => import("./pages/public/IndustrialPropertyAppraisal"));
const MultiFamilyAppraisal = lazyRetry(() => import("./pages/public/MultiFamilyAppraisal"));
const MixedUseAppraisal = lazyRetry(() => import("./pages/public/MixedUseAppraisal"));
const CondoAppraisal = lazyRetry(() => import("./pages/public/CondoAppraisal"));
const InvestmentPropertyAppraisal = lazyRetry(() => import("./pages/public/InvestmentPropertyAppraisal"));
const NewConstructionAppraisal = lazyRetry(() => import("./pages/public/NewConstructionAppraisal"));
const ManufacturedHomeAppraisal = lazyRetry(() => import("./pages/public/ManufacturedHomeAppraisal"));
const FsboAppraisal = lazyRetry(() => import("./pages/public/FsboAppraisal"));
const EminentDomainAppraisal = lazyRetry(() => import("./pages/public/EminentDomainAppraisal"));
const CasualtyLossAppraisal = lazyRetry(() => import("./pages/public/CasualtyLossAppraisal"));
const Articles = lazyRetry(() => import("./pages/public/Articles"));
const ArticlePage = lazyRetry(() => import("./pages/public/ArticlePage"));
const About = lazyRetry(() => import("./pages/About"));
const Terms = lazyRetry(() => import("./pages/Terms"));
const Privacy = lazyRetry(() => import("./pages/Privacy"));
const News = lazyRetry(() => import("./pages/News"));
const NewsPost = lazyRetry(() => import("./pages/NewsPost"));
const Resources = lazyRetry(() => import("./pages/Resources"));
const Glossary = lazyRetry(() => import("./pages/Glossary"));
const GlossaryTerm = lazyRetry(() => import("./pages/GlossaryTerm"));
const URAREditor = lazyRetry(() => import("./URAR/pages/URAREditor"));
import { Helmet, HelmetProvider } from "react-helmet-async";
import { GoogleAnalytics } from "@/components/GoogleAnalytics";

const queryClient = new QueryClient();

// Lazy-loaded analyzer views
const SketchView = lazyRetry(() => import("./pages/appraisal/SketchView"));
const DCFProView = lazyRetry(() => import("./pages/appraisal/DCFProView"));
const DirectCapView = lazyRetry(() => import("./pages/appraisal/DirectCapView"));
const VerifyReport = lazyRetry(() => import("./pages/VerifyReport"));

// Component to add noindex meta tag to dashboard pages
function DashboardMeta() {
  return (
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );
}

/**
 * Legacy redirect: /site/:subdomain/* → https://subdomain.ashlarapp.com/*
 * Performs a 301-equivalent client-side redirect.
 */
function LegacySiteRedirect() {
  const { subdomain, '*': rest } = useParams();
  const location = useLocation();
  
  if (subdomain) {
    const path = rest ? `/${rest}` : '/';
    const target = getSiteUrl(subdomain, path);
    // Only redirect in production (subdomain environment)
    if (window.location.hostname.endsWith('.ashlarapp.com') || window.location.hostname === 'ashlarapp.com') {
      window.location.replace(target);
      return null;
    }
  }
  
  // In dev, render the old path-based routes
  return null;
}

// Check for subdomain site on app load
const subdomainInfo = getSubdomainInfo();

const SubdomainBootFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const App = () => {
  // If on a subdomain site, render only the public site router
  if (subdomainInfo.isSubdomainSite && subdomainInfo.subdomain) {
    return (
      <Suspense fallback={<SubdomainBootFallback />}>
        <SubdomainSiteRouter subdomain={subdomainInfo.subdomain} />
      </Suspense>
    );
  }

  // If on a custom domain, render the site router with domain resolution
  if (subdomainInfo.isSubdomainSite && subdomainInfo.customDomain) {
    return (
      <Suspense fallback={<SubdomainBootFallback />}>
        <SubdomainSiteRouter customDomain={subdomainInfo.customDomain} />
      </Suspense>
    );
  }

  return (
  <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <QueryClientProvider client={queryClient}>
        <RealtimeProvider>
        <AuthProvider>
          <TeamProvider>
          <AdminViewAsProvider>
             <ExportProgressProvider>
            <OfflineBanner />
            <ReloadPrompt />
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ScrollToTop />
              <GoogleAnalytics />
              <CookieConsent />
              <TourProvider>
              <main id="main-content">
              <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
                <Route path="/login" element={<Login />} />
                {DevShellPreview && <Route path="/_preview/shell" element={<DevShellPreview />} />}
                {DevSketchV2 && <Route path="/_preview/sketch-v2" element={<DevSketchV2 />} />}
                {DevPaletteShowroom && <Route path="/_preview/palette" element={<DevPaletteShowroom />} />}
                {DevBrandDirections && <Route path="/_preview/brand" element={<DevBrandDirections />} />}
                {DevMarkLab && <Route path="/_preview/mark" element={<DevMarkLab />} />}
                {DevReportSample && <Route path="/_preview/report-sample" element={<DevReportSample />} />}
                {DevLLMNarrative && <Route path="/_preview/llm-narrative" element={<DevLLMNarrative />} />}
                {import.meta.env.DEV && <Route path="/_preview/tax-appeal" element={<TaxAppealFeasibility />} />}
                {import.meta.env.DEV && <Route path="/_preview/market-report" element={<MarketDataReport />} />}
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                {/* /auth/complete-profile removed - license entry is now in Profile */}
                <Route path="/features" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <Features />} />
                <Route path="/pricing" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <Pricing />} />
                <Route path="/about" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <About />} />
                <Route path="/terms" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <Terms />} />
                <Route path="/privacy" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <Privacy />} />
                {/* Consumer funnel + professional hub (marketplace fork) */}
                <Route path="/find-an-appraiser" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <FindAnAppraiser />} />
                <Route path="/for-appraisers" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <ForAppraisers />} />
                <Route path="/get-found" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <GetFound />} />
                <Route path="/why-ashlar" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <WhyAshlar />} />
                <Route path="/for-attorneys" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <ForAttorneys />} />
                <Route path="/for-homeowners" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <ForHomeowners />} />
                <Route path="/for-investors" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <ForInvestors />} />
                <Route path="/for-cpas" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <ForCpas />} />
                <Route path="/estate-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <EstateAppraisal />} />
                <Route path="/divorce-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <DivorceAppraisal />} />
                <Route path="/property-tax-appeal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <PropertyTaxAppeal />} />
                <Route path="/pre-listing-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <PreListingAppraisal />} />
                <Route path="/pmi-removal-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <PmiRemovalAppraisal />} />
                <Route path="/retrospective-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <RetrospectiveAppraisal />} />
                <Route path="/litigation-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <LitigationAppraisal />} />
                <Route path="/gift-tax-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <GiftTaxAppraisal />} />
                <Route path="/relocation-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <RelocationAppraisal />} />
                <Route path="/bankruptcy-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <BankruptcyAppraisal />} />
                <Route path="/land-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <LandAppraisal />} />
                <Route path="/commercial-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <CommercialAppraisal />} />
                <Route path="/purchase-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <PurchaseAppraisal />} />
                <Route path="/refinance-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <RefinanceAppraisal />} />
                <Route path="/portfolio-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <PortfolioAppraisal />} />
                <Route path="/office-building-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <OfficeBuildingAppraisal />} />
                <Route path="/retail-property-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <RetailPropertyAppraisal />} />
                <Route path="/industrial-property-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <IndustrialPropertyAppraisal />} />
                <Route path="/multi-family-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <MultiFamilyAppraisal />} />
                <Route path="/mixed-use-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <MixedUseAppraisal />} />
                <Route path="/condo-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <CondoAppraisal />} />
                <Route path="/investment-property-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <InvestmentPropertyAppraisal />} />
                <Route path="/new-construction-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <NewConstructionAppraisal />} />
                <Route path="/manufactured-home-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <ManufacturedHomeAppraisal />} />
                <Route path="/fsbo-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <FsboAppraisal />} />
                <Route path="/eminent-domain-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <EminentDomainAppraisal />} />
                <Route path="/casualty-loss-appraisal" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <CasualtyLossAppraisal />} />
                <Route path="/articles" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <Articles />} />
                <Route path="/articles/:slug" element={IS_PWA ? <Navigate to="/dashboard" replace /> : <ArticlePage />} />
                <Route path="/news" element={<News />} />
                <Route path="/news/:slug" element={<NewsPost />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/glossary" element={<Glossary />} />
                <Route path="/glossary/:slug" element={<GlossaryTerm />} />

                {/* Public report verification (QR / short-URL target). No auth; noindex. */}
                <Route path="/verify/:code" element={<VerifyReport />} />

                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <Dashboard />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/home"
                  element={
                    <ProtectedRoute>
                      <DashboardMeta />
                      <PendingHome />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <MyBusiness />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <Analytics />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <DashboardMeta />
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/company"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <Company />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <DashboardMeta />
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <DashboardMeta />
                      <Notifications />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/team"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <Team />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Engagement Letter Editor — admin/moderator only (feature gated for overhaul) */}
                <Route
                  path="/engagement/:id/edit"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <EngagementEditor />
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Engagement Letter Log — admin/moderator only (feature gated for overhaul) */}
                <Route
                  path="/engagements/log"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <DashboardMeta />
                        <EngagementLog />
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Appraisals - verified users only */}
                <Route
                  path="/appraisals"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <Appraisals />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/appraisals/:id/edit"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <AppraisalEditor />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<EditorPanels />} />
                  <Route path="sketch" element={<Suspense fallback={null}><SketchView /></Suspense>} />
                  <Route path="dcf/:instanceId" element={<Suspense fallback={null}><DCFProView /></Suspense>} />
                  <Route path="direct-cap" element={<Suspense fallback={null}><DirectCapView /></Suspense>} />
                </Route>
                <Route
                  path="/appraisals/:id/recap"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <PropertyRecap />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/appraisals/log"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <AppraisalLog />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/appraisals/trash"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <AppraisalTrash />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/appraisals/tools"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <AppraisalTools />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/appraisals/tools/tax-appeal"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <TaxAppealFeasibility />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/appraisals/tools/sketch"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <StandaloneSketch />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/appraisals/tools/dcf"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <StandaloneDCF />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/appraisals/tools/market-report"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <MarketDataReport />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/appraisals/portfolios"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <Portfolios />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/appraisals/portfolios/:id"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <PortfolioDetail />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/appraisals/template-builder"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <TemplateBuilder />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/appraisals/component-library"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <ComponentLibrary />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Admin - admin only */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <DashboardMeta />
                      <AdminRoute>
                        <Admin />
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Site Builder - verified users only */}
                <Route
                  path="/dashboard/site-builder"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <SiteBuilder />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Leads - verified users only */}
                <Route
                  path="/leads"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <Leads />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Calendar - verified/team users only */}
                <Route
                  path="/calendar"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <Calendar />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Mileage */}
                <Route
                  path="/mileage"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <Mileage />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Referrals */}
                <Route
                  path="/referrals"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <Referrals />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Trainee Experience Log */}
                <Route
                  path="/trainee/experience-log"
                  element={
                    <ProtectedRoute>
                      <DashboardMeta />
                      <TraineeExperienceLog />
                    </ProtectedRoute>
                  }
                />
                {/* How-To Guides */}
                <Route
                  path="/guides"
                  element={
                    <ProtectedRoute>
                      <VerifiedRoute>
                        <DashboardMeta />
                        <Guides />
                      </VerifiedRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Legacy /site/ routes — redirect to subdomain in production, render in dev */}
                <Route path="/site/:subdomain/*" element={<LegacySiteRedirect />} />
                <Route path="/site/:subdomain" element={<PublicSite />} />
                <Route path="/site/:subdomain/blog" element={<PublicSiteBlog />} />
                <Route path="/site/:subdomain/blog/:slug" element={<PublicSiteBlogPost />} />
                <Route path="/site/:subdomain/glossary/:termSlug" element={<PublicSite />} />
                <Route path="/site/:subdomain/:pageSlug" element={<PublicSite />} />
                {/* Public appraiser directory - no auth required */}
                <Route path="/directory" element={<AppraiserDirectory />} />
                <Route path="/directory/:slug" element={<AppraiserDirectoryProfile />} />
                {/* Public share routes - no auth required */}
                <Route path="/share/:token" element={<SharedAppraisal />} />
                <Route path="/engagement/:token" element={<SharedEngagement />} />
                <Route path="/sign-report/:token" element={<SharedReport />} />
                {/* URAR v2 editor */}
                <Route
                  path="/urar/:reportId"
                  element={
                    <ProtectedRoute>
                      <DashboardMeta />
                      <AdminRoute>
                        <URAREditor />
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              </main>
              </TourProvider>
            </BrowserRouter>
            </ExportProgressProvider>
          </AdminViewAsProvider>
          </TeamProvider>
        </AuthProvider>
        </RealtimeProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
);
};

export default App;
